import React, { useRef, useState, useEffect, useCallback } from 'react';

interface ClipPlayerProps {
  src: string;
  startTime: number;
  endTime: number;
  markerTime: number; // The actual marker/event time
  autoPlay?: boolean;
  onEnded?: () => void;
  onClose?: () => void;
  className?: string;
  isExpanded?: boolean;
  onToggleExpand?: () => void;
  initialRangeBefore?: number;
  initialRangeAfter?: number;
}

/**
 * ClipPlayer - A video player for playing specific segments/clips
 * Shows progress bar relative to the clip duration (0 to segment length)
 * instead of the full video duration
 */
const ClipPlayer: React.FC<ClipPlayerProps> = ({
  src,
  startTime: initialStartTime,
  endTime: initialEndTime,
  markerTime,
  autoPlay = true,
  onEnded,
  onClose,
  className = '',
  isExpanded = false,
  onToggleExpand,
  initialRangeBefore = 5,
  initialRangeAfter = 5,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isPlaying, setIsPlaying] = useState(autoPlay);
  const [isLoading, setIsLoading] = useState(true);
  
  // Scroll into view when expanded
  useEffect(() => {
    if (isExpanded && containerRef.current) {
      containerRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [isExpanded]);
  
  // Local range state for this clip
  const [rangeBefore, setRangeBefore] = useState(initialRangeBefore);
  const [rangeAfter, setRangeAfter] = useState(initialRangeAfter);
  
  // Calculate actual start/end times based on local range
  const startTime = Math.max(0, markerTime - rangeBefore);
  const endTime = markerTime + rangeAfter;
  const duration = endTime - startTime;
  
  const [currentTime, setCurrentTime] = useState(0); // Relative to clip start

  // Format time as MM:SS or just SS for short clips
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    if (mins > 0) {
      return `${mins}:${secs.toString().padStart(2, '0')}`;
    }
    return `0:${secs.toString().padStart(2, '0')}`;
  };

  // Handle range change - seek to new start time
  const handleRangeChange = useCallback((newBefore: number, newAfter: number) => {
    const video = videoRef.current;
    if (!video) return;
    
    setRangeBefore(newBefore);
    setRangeAfter(newAfter);
    
    // Seek to new start time
    const newStartTime = Math.max(0, markerTime - newBefore);
    video.currentTime = newStartTime;
    setCurrentTime(0);
  }, [markerTime]);

  // Handle time updates
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleTimeUpdate = () => {
      const relativeTime = video.currentTime - startTime;
      setCurrentTime(Math.max(0, Math.min(relativeTime, duration)));

      // Auto-stop at end time (don't auto-close, let user interact)
      if (video.currentTime >= endTime) {
        video.pause();
        setIsPlaying(false);
        // Don't call onEnded - keep player open for user interaction
      }
    };

    const handleLoadedData = () => {
      setIsLoading(false);
      // Seek to start time
      video.currentTime = startTime;
      if (autoPlay) {
        video.play().catch(() => setIsPlaying(false));
      }
    };

    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const handleWaiting = () => setIsLoading(true);
    const handleCanPlay = () => setIsLoading(false);

    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('loadeddata', handleLoadedData);
    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    video.addEventListener('waiting', handleWaiting);
    video.addEventListener('canplay', handleCanPlay);

    return () => {
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('loadeddata', handleLoadedData);
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('waiting', handleWaiting);
      video.removeEventListener('canplay', handleCanPlay);
    };
  }, [startTime, endTime, duration, autoPlay, onEnded]);

  // Toggle play/pause
  const togglePlay = (e: React.MouseEvent) => {
    e.stopPropagation();
    const video = videoRef.current;
    if (!video) return;

    if (isPlaying) {
      video.pause();
    } else {
      // If at end, restart from beginning
      if (video.currentTime >= endTime) {
        video.currentTime = startTime;
      }
      video.play().catch(() => {});
    }
  };

  // Handle progress bar click
  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    const video = videoRef.current;
    if (!video) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const percentage = clickX / rect.width;
    const newRelativeTime = percentage * duration;
    video.currentTime = startTime + newRelativeTime;
  };

  // Calculate progress percentage
  const progressPercent = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div ref={containerRef} className={`relative bg-black ${className}`} onClick={(e) => e.stopPropagation()}>
      {/* Video Element (hidden controls) - click to play/pause */}
      <video
        ref={videoRef}
        src={src}
        className="w-full h-full object-contain cursor-pointer"
        preload="auto"
        playsInline
        disableRemotePlayback
        disablePictureInPicture
        onClick={togglePlay}
      />

      {/* Loading Overlay */}
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50">
          <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {/* Custom Controls Overlay */}
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2">
        {/* Expanded Controls - Range Selector */}
        {isExpanded && (
          <div className="flex items-center justify-center space-x-4 mb-3 pb-2 border-b border-white/20">
            <div className="flex items-center space-x-2">
              <span className="text-white/80 text-xs">Range:</span>
              <input
                type="number"
                min="0"
                max="60"
                value={rangeBefore}
                onChange={(e) => {
                  e.stopPropagation();
                  handleRangeChange(Math.max(0, Math.min(60, parseInt(e.target.value) || 0)), rangeAfter);
                }}
                onClick={(e) => e.stopPropagation()}
                className="w-12 px-1.5 py-0.5 text-xs bg-white/20 text-white border border-white/30 rounded focus:outline-none focus:ring-1 focus:ring-white/50"
              />
              <span className="text-white/60 text-xs">s before</span>
              <span className="text-white/40 text-xs">/</span>
              <input
                type="number"
                min="0"
                max="60"
                value={rangeAfter}
                onChange={(e) => {
                  e.stopPropagation();
                  handleRangeChange(rangeBefore, Math.max(0, Math.min(60, parseInt(e.target.value) || 0)));
                }}
                onClick={(e) => e.stopPropagation()}
                className="w-12 px-1.5 py-0.5 text-xs bg-white/20 text-white border border-white/30 rounded focus:outline-none focus:ring-1 focus:ring-white/50"
              />
              <span className="text-white/60 text-xs">s after</span>
            </div>
          </div>
        )}

        {/* Progress Bar */}
        <div
          className="h-1 bg-white/30 rounded-full cursor-pointer mb-2 group"
          onClick={handleProgressClick}
        >
          <div
            className="h-full bg-white rounded-full relative transition-all"
            style={{ width: `${progressPercent}%` }}
          >
            <div className="absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity" />
          </div>
        </div>

        {/* Controls Row */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            {/* Play/Pause Button */}
            <button
              onClick={togglePlay}
              className="text-white hover:text-gray-200 transition-colors"
            >
              {isPlaying ? (
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8 5v14l11-7z" />
                </svg>
              )}
            </button>

            {/* Time Display */}
            <span className="text-white text-xs font-medium">
              {formatTime(currentTime)} / {formatTime(duration)}
            </span>
          </div>

          <div className="flex items-center space-x-2">
            {/* Expand/Collapse Button */}
            {onToggleExpand && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleExpand();
                }}
                className="text-white hover:text-gray-200 transition-colors"
                title={isExpanded ? 'Collapse' : 'Expand'}
              >
                {isExpanded ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 9V4.5M9 9H4.5M9 9L3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0-4.5l5.25 5.25" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15" />
                  </svg>
                )}
              </button>
            )}

            {/* Close Button */}
            {onClose && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onClose();
                }}
                className="text-white hover:text-gray-200 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClipPlayer;

